/**
 * Reaction stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const reactionStickersPack: ToilePack = {
    slug: 'reaction-stickers',
    category: 'sticker',
    name: 'Reaction stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'lol', label: 'LOL', subject: 'A burst shape lettered "LOL" in bold with laughing tears'},
        {key: 'omg', label: 'OMG', subject: 'A speech bubble lettered "OMG" with wide eyes'},
        {key: 'no', label: 'NO', subject: 'A raised hand with the word "NO" lettered beneath'},
    ],
    styles: [
        {key: 'chat', label: 'Chat sticker', style: 'messaging-app sticker, expressive cartoon, thick outline, bold flat colour', palette: 'Bright flat colour with a white outline', framing: 'The sticker isolated on a plain ground with a white outline, built to read at chat size'},
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['as a WOW burst', 'in a single bold colour', 'for a sticker sheet'],
    directionHint: 'try: as a WOW burst',
    seo: {
        title: 'Reaction Stickers: LOL Burst, OMG, NO With a Hand',
        metaDescription: 'Nine reaction stickers — a lettered LOL burst, a lettered OMG, a lettered NO with a hand — in chat, kawaii, and doodle styles.',
        h1: 'Reaction stickers: LOL burst, OMG, and NO in three styles',
        tagline: 'Nine text reaction stickers, three bursts, three styles each.',
        intro: "The reaction as a sticker: a lettered LOL burst, a lettered OMG, a lettered NO with a hand held up. Rendered chat, kawaii, and doodle so the burst matches whatever your chat's meme energy is. Ask for a different reaction word — WOW, BRB, YAS — and the same three styles carry it.",
        useCases: [
            'a group chat reaction sticker',
            'a meme sticker layer for messages',
            'a scrapbook or journal reaction accent',
            'a printable sticker sheet of chat reactions',
            'a gift sticker set for a texting friend',
        ],
        faq: [
            {q: 'Are these full characters, or just lettered text bursts?', a: 'These are lettered text bursts with a graphic treatment (comic-style LOL, OMG, NO) — no character, unlike the platform chat-sticker packs (WhatsApp, Discord, etc.).'},
            {q: 'Can I get a different reaction word?', a: 'Yes — the direction field takes any short reaction word, lettered in the same three styles.'},
            {q: "What's the difference between chat, kawaii, and doodle?", a: "Chat leans toward a bold comic-burst look, kawaii is softer and rounder, and doodle is a hand-drawn sketch style — pick whichever matches your chat's energy."},
            {q: 'How is this different from the cat reaction sticker pack?', a: "This pack is lettered text bursts with no character; the cat reaction pack uses a cat's expression instead of words — pick whichever communicates better for you."},
            {q: 'Can I use these in a scrapbook or journal, not just chat?', a: 'Yes — the bold lettered look works equally well as a page accent.'},
        ],
    },
}
